import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Check, X, Clock } from "lucide-react";

const STATUS_BADGE = {
  bekliyor: { label: "Bekliyor", cls: "bg-amber-500 hover:bg-amber-500" },
  onaylandi: { label: "Onaylandı", cls: "bg-emerald-600 hover:bg-emerald-600" },
  reddedildi: { label: "Reddedildi", cls: "bg-rose-600 hover:bg-rose-600" },
};

export default function DeleteRequests() {
  const { user } = useAuth();
  const [status, setStatus] = useState("bekliyor");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState({});
  const isAdmin = user.role === "super_admin";

  const load = useCallback(() => {
    setData(null);
    api.get("/voters/delete-requests", { params: { status } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Talepler yüklenemedi"));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const decide = async (r, action) => {
    setBusy(r.id);
    try {
      await api.post(`/voters/delete-requests/${r.id}/${action}`, { note: note[r.id] || null });
      toast.success(action === "approve" ? `${r.company_name} silindi` : "Talep reddedildi");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "İşlem başarısız");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="delete-requests-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl grid place-items-center bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300">
            <Trash2 className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0F2C59] dark:text-sky-300">Silme Talepleri</h1>
            <p className="text-sm text-muted-foreground">
              {isAdmin ? "Komite yöneticilerinden gelen firma silme talepleri" : "Oluşturduğun silme taleplerinin durumu"}
            </p>
          </div>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[190px]" data-testid="dr-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="bekliyor">Bekleyenler</SelectItem>
            <SelectItem value="onaylandi">Onaylananlar</SelectItem>
            <SelectItem value="reddedildi">Reddedilenler</SelectItem>
            <SelectItem value="all">Tümü</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!data ? (
        <Skeleton className="h-48 w-full" />
      ) : data.rows.length === 0 ? (
        <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="dr-empty">
          Bu durumda talep yok.
        </Card>
      ) : (
        <div className="space-y-2">
          {data.rows.map((r) => (
            <Card key={r.id} className="p-3 space-y-2" data-testid={`dr-row-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-sm">{r.company_name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    Komite {r.committee_no}{r.branch ? ` · ${r.branch}` : ""}{r.sicil_no ? ` · Sicil ${r.sicil_no}` : ""} · Talep: {r.requested_by_name}
                    {r.decided_by_name ? ` · Karar: ${r.decided_by_name}` : ""}
                  </p>
                </div>
                <Badge className={`text-[10px] ${STATUS_BADGE[r.status]?.cls || ""}`}>{STATUS_BADGE[r.status]?.label || r.status}</Badge>
              </div>
              <p className="text-xs bg-muted/50 rounded-md px-2 py-1.5"><b>Gerekçe:</b> {r.reason}</p>
              {r.decision_note && <p className="text-xs text-muted-foreground"><b>Karar notu:</b> {r.decision_note}</p>}
              {isAdmin && r.status === "bekliyor" && (
                <div className="flex flex-wrap items-center gap-2">
                  <Input value={note[r.id] || ""} onChange={(e) => setNote((n) => ({ ...n, [r.id]: e.target.value }))}
                    placeholder="Karar notu (opsiyonel)" className="h-8 text-xs max-w-xs" data-testid={`dr-note-${r.id}`} />
                  <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" disabled={busy === r.id}
                    onClick={() => decide(r, "approve")} data-testid={`dr-approve-${r.id}`}>
                    <Check className="h-3.5 w-3.5 mr-1" />Onayla ve Sil
                  </Button>
                  <Button size="sm" variant="outline" className="h-8" disabled={busy === r.id}
                    onClick={() => decide(r, "reject")} data-testid={`dr-reject-${r.id}`}>
                    <X className="h-3.5 w-3.5 mr-1" />Reddet
                  </Button>
                </div>
              )}
              {!isAdmin && r.status === "bekliyor" && (
                <p className="text-[11px] text-amber-700 dark:text-amber-400 flex items-center gap-1">
                  <Clock className="h-3 w-3" />Genel Yönetici onayı bekleniyor
                </p>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
